import { useMemo, useState, useCallback, useEffect } from 'react';
import PageWrapper from '../../../components/Display/PageWrapper';
import ChartContainer from './ChartContainer';
import styles from "../../../components/Display/Wrapper.module.scss";
import AnalyticsSection from '../../../components/Display/AnalyticsSection';
import SmallerDataItem from './SmallerDataItem';
import { useAccountContext } from '../../../contexts/AccountContext';
import { privateFetch } from '../../../utils/apiHelper';



const SECTION_LABELS = {
  general: "Vue générale",
  secondary: "Analyse des sessions",
  summary: "En bref"
};

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Helper pour calculer les dates selon la temporalité
const getDateRange = (temporality) => {
  const end = new Date();
  const start = new Date();

  switch (temporality) {
    case 'week': start.setDate(end.getDate() - 7); break;
    case 'month': start.setMonth(end.getMonth() - 1); break;
    case 'semester': start.setMonth(end.getMonth() - 6); break;
    case 'year': start.setFullYear(end.getFullYear() - 1); break;
    case 'all':
    default:
      // Pour 'all', on ne passe pas le paramètre start_dt
      return { start: null, end: null };
  }

  return {
    start: formatDate(start),
    end: formatDate(end)
  };
};


function AnalyticsPage({ isActive, config, disable }) {


  const { accountContext } = useAccountContext();
  const [temporality, setTemporality] = useState("all");
  const [analyticsData, setAnalyticsData] = useState({}); // Stocke les données de tous les graphes
  const [loading, setLoading] = useState(false);

  // 1. Calcul des dates quand la temporalité change
  const { start, end } = useMemo(() => getDateRange(temporality), [temporality]);

  // 2. Fonction de fetch centralisée
  const fetchAllData = useCallback(async () => {
    if (disable || !isActive || !config || config.length === 0) return;

    setLoading(true);

    try {
      const promises = config.map(async (c) => {
        const key = c.key;

        let url = `/cu/${accountContext.id}/${c.endPoint}/?scope=${c.scope}`;

        if (c.path) {
          url += `&path=${encodeURIComponent(c.path)}`;
        }

        if (start) {
          url += `&start_dt=${start}`;
        }

        if (end) {
          url += `&end_dt=${end}`;
        }

        const response = await privateFetch(
          'GET',
          'analytics',
          url
        );

        // Selon l'implémentation de privateFetch :
        // - fetch classique => response peut déjà être le JSON
        // - axios => response.data contient le JSON
        const data = response?.data ?? response;

        return {
          key,
          data,
          config: c
        };
      });

      const results = await Promise.all(promises);

      const newDataMap = {};

      results.forEach(({ key, data, config }) => {
        newDataMap[key] = {
          data,
          config
        };
      });


      setAnalyticsData(newDataMap);

    } catch (error) {
      console.error(
        "Erreur lors du chargement des analytics",
        error
      );
    } finally {
      setLoading(false);
    }
  }, [
    config,
    accountContext.id,
    start,
    end,
    isActive,
    disable
  ]);

  // 3. Déclenchement du fetch
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const groupedCharts = useMemo(() => {
    if (!config) return {};

    const groups = {};

    config.forEach((c) => {
      const type = c.displayType || 'general'; // Valeur par défaut si non défini

      if (!groups[type]) {
        groups[type] = [];
      }

      const key = c.key;
      const chartData = analyticsData[key];
      const isChartLoading = loading || !chartData;

      groups[type].push({
        ...c,
        data: chartData?.data,
        isLoading: isChartLoading
      });
    });
    console.log("AnalyticsPage", groups);

    return groups;
  }, [config, analyticsData, loading]);

  const temporalityActions = [
    { label: '7 jours', isSelected: temporality === "week", onClick: () => setTemporality("week") },
    { label: '1 mois', isSelected: temporality === "month", onClick: () => setTemporality("month") },
    { label: '6 mois', isSelected: temporality === "semester", onClick: () => setTemporality("semester") },
    { label: '12 mois', isSelected: temporality === "year", onClick: () => setTemporality("year") },
    { label: 'Depuis le début', isSelected: temporality === "all", onClick: () => setTemporality("all") },
  ];

  if (disable) {
    return (
      <div className={`h-full w-full flex-col ${isActive ? 'flex' : 'hidden'}`}>
        <PageWrapper
          label="Mesure d'audience"
          actions={null}
        >
          <p style={{ marginTop: "30px" }}>Cette page est en cours de construction, elle est momentanément inaccessible.</p>
          <p>Merci de votre patience.</p>
        </PageWrapper>
      </div>
    )
  }

  return (
    <div className={`h-full w-full flex-col ${isActive ? 'flex' : 'hidden'}`}>
      <PageWrapper
        label="Mesure d'audience"
        actions={temporalityActions}
      >
        <div className={styles.scrollableSectionContainer}>


          {Object.entries(groupedCharts).map(([displayType, charts]) => {
            // Si aucune config pour ce type, on affiche rien
            if (charts.length === 0) return null;

            const sectionLabel = SECTION_LABELS[displayType] || displayType;

            if (displayType === 'summary') {
              return (
                <AnalyticsSection key={displayType} label="En bref">
                  <div className={styles.col3gridLayout}>
                    {charts.map((c) => {
                      const value = c.data?.[c.dataKey] ?? null;

                      return (
                        <SmallerDataItem
                          key={c.key}
                          data={value}
                          label={c.label}
                          info={c.info}
                          format={c.format}
                        />
                      );
                    })}
                  </div>
                </AnalyticsSection>
              );
            } else {
              return (
                <AnalyticsSection key={displayType} label={sectionLabel}>
                  <div className={`${displayType == "general" ? styles.listLayout : styles.gridLayout}`}>
                    {charts.map((c) => (
                      <ChartContainer
                        label={c.label}
                        type={c.chartType}
                        data={c.data}
                        config={c}
                        isLoading={c.isLoading}
                        key={c.key}
                        info={c.info}
                      />
                    ))}
                  </div>

                </AnalyticsSection>
              );
            }
          })}
        </div>
      </PageWrapper>
    </div>
  );
}

export default AnalyticsPage;
